import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// components
import Grid from '@mui/material/Grid2'
import Tree from 'components/Tree'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// context
import { BusinessProcessesAPIContext } from 'containers/BusinessProcesses/layers/API'
// constants
import { Modals } from 'constants/modals'
// graphql
import {
  BUSINESS_PROCESSES_DELETE_MUTATION,
  BUSINESS_PROCESSES_TREE_UPDATE_MUTATION
} from 'graphql/businessProcess/mutations'
// themes
import { ActiveLink } from 'themes/views/tree'
import { REACT_SORTABLE_TREE_STYLES } from 'themes'
// utils
import { handleError } from 'utils/errors/handlers'

const Placeholder = ({ handleAddChild }) => {
  return (
    <Grid container justifyContent={'center'} alignItems={'center'} direction={'column'}>
      <Grid>There is no business process here.</Grid>
      <Grid>
        Click <ActiveLink onClick={() => handleAddChild({})}>here</ActiveLink> to create your first business process
      </Grid>
    </Grid>
  )
}

const BusinessProcessesTree = () => {
  const [deleteMutation] = useMutation(BUSINESS_PROCESSES_DELETE_MUTATION)
  const [treeUpdateMutation] = useMutation(BUSINESS_PROCESSES_TREE_UPDATE_MUTATION)

  const { showModal } = useShowModal({ modalType: Modals.BUSINESS_ENTITY_MODAL })
  const { showModal: showConfirm } = useShowModal({ modalType: Modals.CONFIRM_DELETE_MODAL })

  const { businessProcesses, isLoading, refetch } = React.useContext(BusinessProcessesAPIContext)

  const synchronizeTree = async (tree) => {
    try {
      await treeUpdateMutation({
        variables: {
          businessProcesses: tree.map(({ id, parentId, position, expanded }) => ({
            id,
            parentId,
            position,
            expanded
          }))
        }
      })
    } catch (errors) {
      handleError(errors, 'Tree')
    }
  }

  const handleDelete = ({ node }) =>
    showConfirm({
      width: 'sm',
      handleConfirm: async () => {
        try {
          await deleteMutation({
            variables: {
              id: node.id
            }
          })

          await refetch()
        } catch (errors) {
          handleError(errors, 'BusinessProcessesTree.handleDelete')
        }
      }
    })

  const handleEdit = ({ node }) =>
    showModal({
      width: 'sm',
      item: node
    })

  const handleAddChild = ({ node }) =>
    showModal({
      width: 'sm',
      parent: node
    })

  const generateActionHandlers = () => ({
    actionsHandlers: {
      handleEditClick: ({ node }) => handleEdit({ node }),
      handleDeleteClick: ({ node }) => handleDelete({ node }),
      handleAddChildClick: ({ node }) => handleAddChild({ node })
    }
  })

  return (
    <>
      <Tree
        refetch={refetch}
        isLoading={isLoading}
        source={businessProcesses}
        synchronizeTree={synchronizeTree}
        generateActionHandlers={generateActionHandlers}
        placeholder={<Placeholder handleAddChild={handleAddChild} />}
        reactVirtualizedListProps={{
          style: REACT_SORTABLE_TREE_STYLES
        }}
      />
      <ConfirmDeleteModal header={'Delete Business Process'}>
        Are you sure you want to delete this business process?
      </ConfirmDeleteModal>
    </>
  )
}

export default BusinessProcessesTree
